
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Message, mapDBMessageToUIMessage, MessageTone } from "@/types/message";
import { useConversationState } from "@/hooks/conversation/use-conversation-state";
import ConversationContent from "@/components/refund/conversation/ConversationContent";

const Conversation = () => {
  const {
    id,
    threadRef,
    error,
    isClarificationSubmitting,
    setIsClarificationSubmitting,
    conversationData
  } = useConversationState();

  const onRegenerateAIResponse = async (messageId: string, tone: MessageTone, customInstructions?: string): Promise<Message | null> => {
    return await conversationData.handleRegenerateAIResponse(messageId, tone, customInstructions);
  };

  const handleFormatUpdate = (format: "email" | "chat") => {
    // Update the conversation format in the hook to prevent overwrites
    if (conversationData.updateConversationFormat) {
      conversationData.updateConversationFormat(format);
    }
  };

  if (conversationData.loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container py-8 flex items-center justify-center min-h-[400px]">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !conversationData.conversation) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container py-8">
          <div className="text-center py-10">
            <h2 className="text-2xl font-bold mb-4">Conversation Not Found</h2>
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                {error || "The requested conversation could not be found or you may not have access to it."}
              </AlertDescription>
            </Alert>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <Link to="/conversations">View All Conversations</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/refund">Create New Refund Request</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const uiMessages: Message[] = conversationData.messages.map(mapDBMessageToUIMessage);

  return (
    <div className="min-h-screen bg-white">
      <ConversationContent
        conversation={conversationData.conversation}
        uiMessages={uiMessages}
        dbMessages={conversationData.messages}
        threadRef={threadRef}
        conversationData={conversationData}
        isClarificationSubmitting={isClarificationSubmitting}
        setIsClarificationSubmitting={setIsClarificationSubmitting}
        onRegenerateAIResponse={onRegenerateAIResponse}
        onFormatUpdate={handleFormatUpdate}
      />
    </div>
  );
};

export default Conversation;
